import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { message } from 'antd';
import { setCurrInfoWindow, setCurrPOIs } from 'features/vicinityMapSlice';
import { ICategory, IPOI, IVicinityMapData } from 'lib/models/vicinityMap.interface';
import React from 'react';
import config from '../config';
import { DEFAULT_PRIMARY_COLOR, DEFAULT_SIDE_MENU_WIDTH, DEFAULT_ZOOM_LEVEL } from '../constants';

export const getAllPOIs = (categoriesData: ICategory[]) => {
  let allPOIs: IPOI[] = [];
  categoriesData.forEach((category) => {
    category.pois.forEach((poi) => {
      poi.coordinates && allPOIs.push(poi);
    });
  });
  return allPOIs;
};

export const renderDirections = ({
  directionsRenderer,
  POI,
  origin,
  map,
  dispatch,
}: {
  directionsRenderer: google.maps.DirectionsRenderer;
  POI: IPOI;
  origin: google.maps.LatLng;
  map: google.maps.Map;
  dispatch: ThunkDispatch<any, undefined, AnyAction> & React.Dispatch<AnyAction>;
}) => {
  const destination = getGMCoordinates(POI.coordinates);
  const mode = POI.mode;

  // removes any current directions from the map
  directionsRenderer.setMap(null);

  // create request object
  const bounds = new google.maps.LatLngBounds();
  bounds.extend(origin);
  bounds.extend(destination);

  // create request object
  const request = {
    origin,
    destination,
    travelMode: mode || google.maps.TravelMode.DRIVING,
  };

  // create a new directions service and route from the origin to the destination
  const directionsService = new google.maps.DirectionsService();
  directionsService.route(request, function (directionsResponse, status) {
    if (status === google.maps.DirectionsStatus.OK) {
      // render polyline directions
      directionsRenderer.setDirections(directionsResponse);

      // sets the directions renderer to use the map
      directionsRenderer.setMap(map);

      const { distance, duration } = directionsResponse!.routes[0].legs[0];

      // fit the map to the new bounds
      map.fitBounds(bounds);
      map.setZoom(map.getZoom()! - 1);
      map.panTo(bounds.getCenter());

      // set the info window with the new POI data
      dispatch(
        setCurrInfoWindow({
          position: getGMCoordinates(POI.coordinates),
          content: {
            title: POI.title,
            address: POI.address,
            directions: `${duration!.text} - ${distance!.text}`,
          },
          image: POI.imgUrlComAsset
            ? `/${POI.imgUrlComAsset[0].folder}/${POI.imgUrlComAsset[0].fileName}`
            : undefined,
          mode: POI.mode || google.maps.TravelMode.DRIVING,
          poiTitle: POI.title,
          poiCategory: POI.vicCategorySysId!,
        })
      );
    }
  });
};

export const handleMapIt = ({
  directionsRenderer,
  origin,
  destination,
  map,
  title,
  address,
  currPOIs,
  dispatch,
}: {
  directionsRenderer: google.maps.DirectionsRenderer;
  origin: google.maps.LatLng;
  destination: google.maps.LatLng;
  map: google.maps.Map;
  title: string;
  address: string;
  currPOIs: IPOI[];
  dispatch: ThunkDispatch<any, undefined, AnyAction> & React.Dispatch<AnyAction>;
}) => {
  // removes any current directions from the map
  directionsRenderer.setMap(null);

  // create request object
  const newBounds = new google.maps.LatLngBounds();
  newBounds.extend(origin);
  newBounds.extend(destination);

  // create request object
  const request = {
    origin,
    destination,
    travelMode: google.maps.TravelMode.DRIVING,
    drivingOptions: {
      departureTime: new Date(),
      trafficModel: google.maps.TrafficModel.OPTIMISTIC,
    },
  };

  // create a new directions service and route from the origin to the destination
  const directionsService = new google.maps.DirectionsService();
  directionsService.route(request, function (directionsResponse, status) {
    if (status === google.maps.DirectionsStatus.OK) {
      // render polyline directions
      directionsRenderer.setDirections(directionsResponse);

      // sets the directions renderer to use the map
      directionsRenderer.setMap(map);

      // add new POI to the map
      dispatch(
        setCurrPOIs([
          ...currPOIs,
          {
            title,
            address,
            coordinates: JSON.stringify(directionsResponse!.routes[0].legs[0].end_location),
            mode: google.maps.TravelMode.DRIVING,
            distance: directionsResponse!.routes[0].legs[0].distance!.text,
            duration: directionsResponse!.routes[0].legs[0].duration!.text,
            // following key/values do not need to be read for temporary POI + Info Window
            sysId: '',
            vicCategorySysId: '',
            imgUrlComAssetsSysId: null,
            orderBy: 0,
            markerUrl: null,
          },
        ])
      );

      // set the info window with the new POI data
      dispatch(
        setCurrInfoWindow({
          position: directionsResponse!.routes[0].legs[0].end_location,
          content: {
            title,
            address,
            directions: `${directionsResponse!.routes[0].legs[0].duration!.text} - ${
              directionsResponse!.routes[0].legs[0].distance!.text
            }`,
          },
          mode: google.maps.TravelMode.DRIVING,
        })
      );

      // fit the map to the new bounds
      map.fitBounds(newBounds);
      map.setZoom(map.getZoom()! - 1);
      map.panTo(newBounds.getCenter());
    } else {
      // set error to true if the request fails
      message.error('Map it failed. Please try again later.');
    }
  });
};

export const getInfoWindowTextWidth = (text: string[]) => {
  // return the longest string width in px
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d')!;
  context.font = '300 13px Roboto, Arial, sans-serif';
  let largestWidth = 0;
  text.forEach((t) => {
    const width = context.measureText(t).width;
    if (width > largestWidth) {
      largestWidth = width;
    }
  });
  return largestWidth;
};

export const setCommunityLogoStyles = (communityLogo: Element) => {
  // remove the close button from the community logo info window and add smaller padding
  communityLogo.parentElement!.parentElement!.parentElement!.querySelector('button')!.remove();
  const infoWindowContent = communityLogo.parentElement!.parentElement!.parentElement!;
  infoWindowContent.setAttribute('style', 'padding: 6px!important;');
};

export const getCategory = (sysId: string, data: IVicinityMapData) => {
  const category = data.categories.find((category) => category.sysId === sysId);
  return category;
};

export const getCategoryIcon = (sysId: string, data: IVicinityMapData) => {
  const category = data.categories.find((category) => category.sysId === sysId);
  return category?.markerUrl + '_Icon';
};

export const getGMCoordinates = (coordinates: string) => {
  return new google.maps.LatLng(
    Number(coordinates.split(',')[0]),
    Number(coordinates.split(',')[1])
  );
};

export const generateStaticMapURL = (data: IVicinityMapData) => {
  const baseURL = 'https://maps.googleapis.com/maps/api/staticmap?';
  const center = `center=${
    data.main.centerOfMapCoordinates || data.main.projectLocationCoordinates
  }`;
  const key = `&key=${config.googleMapsApiKey}`;
  const scale = '&scale=2';
  const removeLandmarksStyle =
    '&style=feature:administrative%7Celement:geometry%7Cvisibility:off&style=feature:poi%7Cvisibility:off&style=feature:road%7Celement:labels.icon%7Cvisibility:off&style=feature:transit%7Cvisibility:off';
  const size = `&size=1280x1280`;

  const zoom = `&zoom=${(data.main.defaultZoomLevel || DEFAULT_ZOOM_LEVEL) - 2}`;

  let markers = `&markers=color:red%7C${data.main.projectLocationCoordinates}&markers=color:${(
    data.main.primaryColor || DEFAULT_PRIMARY_COLOR
  ).replace('#', '0x')}%7Csize:small`;
  getAllPOIs(data.categories).forEach((POI) => {
    markers += `%7C${POI.coordinates}`;
  });

  const style = data.main.staticMapUrl!.split('&format=png')[1].replace('&size=480x360', '') || '';

  return baseURL + center + zoom + scale + markers + style + removeLandmarksStyle + size + key;
};

export const resetGoogleMap = ({ map, data }: { map: google.maps.Map; data: IVicinityMapData }) => {
  if (data.main.defaultZoomLevel) {
    map.setZoom(data.main.defaultZoomLevel);
    // pan the map to the center coordinates
    map.panTo(
      getGMCoordinates(data.main.centerOfMapCoordinates || data.main.projectLocationCoordinates)
    );
    // adjust map center to reflect side menu width
    map.panBy(
      ((-(data.main.sideMenuWidth || DEFAULT_SIDE_MENU_WIDTH) / 100) * window.innerWidth) / 2,
      0
    );
  } else {
    const newMarkers = getAllPOIs(data.categories);
    const bounds = new google.maps.LatLngBounds();
    newMarkers.forEach((marker) => {
      bounds.extend(getGMCoordinates(marker.coordinates));
    });
    bounds.extend(
      getGMCoordinates(data.main.centerOfMapCoordinates || data.main.projectLocationCoordinates)
    );

    // fit the bounds to the map
    map.fitBounds(bounds);

    // pan to center and adjust map center to reflect side menu width
    map.setZoom(map.getZoom()! - 1);
    map.panTo(bounds.getCenter());
    map.panBy(
      data.main.sideMenuPosition === 'left' || null
        ? ((-(data.main.sideMenuWidth || DEFAULT_SIDE_MENU_WIDTH) / 100) * window.innerWidth) / 2
        : (((data.main.sideMenuWidth || DEFAULT_SIDE_MENU_WIDTH) / 100) * window.innerWidth) / 2,
      0
    );
  }
};

export function initSMOverlay({
  map,
  bounds,
  image,
  rotate,
  onClick,
}: {
  map: google.maps.Map;
  bounds: google.maps.LatLngBounds;
  image: string;
  rotate: number;
  onClick: () => void;
}): void {
  // documentation: https://developers.google.com/maps/documentation/javascript/customoverlays?hl=en#add
  class SMOverlay extends google.maps.OverlayView {
    private bounds: google.maps.LatLngBounds;
    private image: string;
    private div?: HTMLElement;

    constructor(bounds: google.maps.LatLngBounds, image: string) {
      super();

      this.bounds = bounds;
      this.image = image;
    }

    /**
     * onAdd is called when the map's panes are ready and the overlay has been
     * added to the map.
     */
    onAdd() {
      this.div = document.createElement('div');
      this.div.style.borderStyle = 'none';
      this.div.style.borderWidth = '0px';
      this.div.style.position = 'absolute';
      this.div.style.cursor = 'pointer';
      this.div.setAttribute('data-cy', 'site-map-overlay');

      // Create the img element and attach it to the div.
      const img = document.createElement('img');

      img.src = this.image;
      img.style.width = '100%';
      img.style.height = '100%';
      img.style.position = 'absolute';
      img.style.transform = `rotate(${rotate}deg)`;
      this.div.appendChild(img);

      // Add the element to the "overlayLayer" pane.
      const panes = this.getPanes()!;

      panes.overlayMouseTarget.appendChild(this.div);

      this.div.addEventListener('click', function () {
        google.maps.event.trigger(this, 'click');
        onClick();
      });
    }

    draw() {
      // We use the south-west and north-east
      // coordinates of the overlay to peg it to the correct position and size.
      // To do this, we need to retrieve the projection from the overlay.
      const overlayProjection = this.getProjection();

      // Retrieve the south-west and north-east coordinates of this overlay
      // in LatLngs and convert them to pixel coordinates.
      // We'll use these coordinates to resize the div.
      const sw = overlayProjection.fromLatLngToDivPixel(this.bounds.getSouthWest())!;
      const ne = overlayProjection.fromLatLngToDivPixel(this.bounds.getNorthEast())!;

      // Resize the image's div to fit the indicated dimensions.
      if (this.div) {
        this.div.style.left = sw.x + 'px';
        this.div.style.top = ne.y + 'px';
        this.div.style.width = ne.x - sw.x + 'px';
        this.div.style.height = sw.y - ne.y + 'px';
      }
    }
  }

  const overlay: SMOverlay = new SMOverlay(bounds, image);

  overlay.setMap(map);
}

// export function initMapIcons({
//   image
// }: {
//   image: string;
// }): void {
//   class MapIcons extends google.maps.OverlayView {
//     private image: string;
//     private div?: HTMLElement;

//     constructor(image: string) {
//       super();

//       this.image = image;
//     }

//     onAdd() {
//       this.div = document.createElement('div');
//       this.div.style.borderStyle = 'none';
//       this.div.style.borderWidth = '0px';
//       this.div.style.position = 'absolute';
//       this.div.style.cursor = 'pointer';

//       const zoomIn = document.createElement('button');
//       const zoomOut = document.createElement('button');
//       const streeView = document.createElement('button');
//       const satelliteView = document.createElement('button');

//       zoomIn.textContent = 'Zoom In';
//       zoomOut.textContent = 'Zoom Out';
//       streeView.textContent = 'Street View';
//       satelliteView.textContent = 'Satellite View';

//       this.div.appendChild(zoomIn);
//       this.div.appendChild(zoomOut);
//       this.div.appendChild(streeView);
//       this.div.appendChild(satelliteView);

//       const panes = this.getPanes()!;
//       panes.overlayLayer.appendChild(this.div);
//     }

//   }
// }
